import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CONSTANTS } from '../../Constants/Constants';
import {
  GocCollaborationRestrictionDialogComponent,
  GocCollaborationRestrictionDialogData
} from '../../Components/dialogs/common/goc-collaboration-restriction-dialog/goc-collaboration-restriction-dialog.component';
import { CollaborationOperationsService } from './collaboration-operations.service';
import { EstimateCloseService } from '../estimate-close.service';
import { TranslateService } from '@ngx-translate/core';

interface IGOCRestrictionRule {
  requiresDiscardBeforeClose: boolean;
}

@Injectable({ providedIn: 'root' })
export class GocCollaborationRestrictionHandlerService {
  private inProgress = false;

  // Extensible map: add future error codes here.
  private readonly rules: Record<string, IGOCRestrictionRule> = {
    '1515': { requiresDiscardBeforeClose: false },
    '9105': { requiresDiscardBeforeClose: true }
  };

  constructor(
    private readonly dialog: MatDialog,
    private readonly translateService: TranslateService,
    private readonly collaborationOperationsService: CollaborationOperationsService,
    private readonly estimateCloseService: EstimateCloseService
  ) {}

  canHandle(errorCode?: string): boolean {
    if (!errorCode) {
      return false;
    }

    return !!this.rules[errorCode];
  }

  async isGocUserRestricted(error: unknown): Promise<boolean> {
    const httpError = this.asHttpErrorResponse(error);
    const errorCode = this.extractErrorCode(httpError);
    const rule = errorCode ? this.rules[errorCode] : undefined;

    if (!rule) {
      return false;
    }

    // If one GOC restriction flow is already active, suppress downstream local error handling.
    if (this.inProgress) {
      return true;
    }

    this.inProgress = true;

    try {
      const dialogData: GocCollaborationRestrictionDialogData = {
        title: this.resolveTitle(),
        message: this.resolveMessage(errorCode, httpError)
      };

      const dialogRef = this.dialog.open(GocCollaborationRestrictionDialogComponent, {
        width: CONSTANTS.DEFAULT_DIALOG_WIDTH,
        disableClose: true,
        data: dialogData
      });

      await firstValueFrom(dialogRef.afterClosed());
      await this.executeRule(rule);
      return true;
    } finally {
      this.inProgress = false;
    }
  }

  private async executeRule(rule: IGOCRestrictionRule): Promise<void> {
    if (rule.requiresDiscardBeforeClose) {
      await this.collaborationOperationsService.performDiscard(true, true);
    }

    await this.estimateCloseService.closeEstimate();
  }

  private asHttpErrorResponse(error: unknown): HttpErrorResponse | null {
    if (error instanceof HttpErrorResponse) {
      return error;
    }

    return null;
  }

  private extractErrorCode(error: HttpErrorResponse | null): string | undefined {
    const payload = error?.error;
    if (!payload || typeof payload !== 'object') {
      return undefined;
    }

    const raw = (payload as Record<string, unknown>)['errorCode'];
    if (typeof raw === 'number') {
      return raw.toString();
    }

    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    }

    return undefined;
  }

  private resolveTitle(): string {
    const translated = this.translateService.instant('ERROR_TITLE');
    return typeof translated === 'string' && translated !== 'ERROR_TITLE' ? translated : CONSTANTS.ERROR_TITLE;
  }

  private resolveMessage(errorCode: string, error: HttpErrorResponse | null): string {
    const key = `ERRORS.${errorCode}`;
    const translated = this.translateService.instant(key);

    if (typeof translated === 'string' && translated.length > 0 && translated !== key) {
      return translated;
    }

    const detail = this.readDetail(error);
    if (detail) {
      return detail;
    }

    const generic = this.translateService.instant('ERRORS.GENERIC');
    if (typeof generic === 'string' && generic.length > 0 && generic !== 'ERRORS.GENERIC') {
      return generic;
    }

    return 'Something went wrong. Please try again.';
  }

  private readDetail(error: HttpErrorResponse | null): string | undefined {
    const payload = error?.error;
    if (!payload || typeof payload !== 'object') {
      return undefined;
    }

    const rawDetail = (payload as Record<string, unknown>)['detail'];
    if (typeof rawDetail !== 'string') {
      return undefined;
    }

    const detail = rawDetail.trim();
    return detail.length > 0 ? detail : undefined;
  }
}
